if (!customElements.get('oe-carrez-color-picker')) {
  customElements.define('oe-carrez-color-picker', class extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-color-trigger]');
      this.input = this.querySelector('[data-color-search]');
      this.list = this.querySelector('[data-color-list]');
      this.popup = this.querySelector('[data-color-popup]');
      this.native = this.querySelector('[data-color-native]');
      this.items = JSON.parse(this.querySelector('[data-color-values]').textContent);
      this.selected = this.native.value;
      // Option-value variants keep every other selected option (including size).
      this.basePrice = JSON.parse(this.getAttribute('data-color-base-price') || 'null');
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
        style: 'currency', currency: this.dataset.currency || 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 2,
      });
      // Picker presentation only; native option values and shared technical/facet labels stay intact.
      this.label = value => {
        if (this.hasAttribute('data-preserve-color-identity')) return value;
        const color = window.OeCarrezColors.find(value);
        if (!color) return value;
        return color.system === 'RAL' ? color.raw : `${color.code} (${color.label.replace(/ C3[234]$/, '')}) - Matt`;
      };
      this.updateSelection();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.trigger.addEventListener('click', () => this.opened ? this.close(true) : this.open(), options);
      this.trigger.addEventListener('keydown', event => {
        if (['ArrowDown', 'ArrowUp'].includes(event.key)) { event.preventDefault(); this.open(); }
      }, options);
      this.input.addEventListener('input', () => this.filter(this.input.value), options);
      // Search input is never a native option change.
      this.input.addEventListener('change', event => event.stopPropagation(), options);
      this.input.addEventListener('keydown', event => this.key(event), options);
      this.querySelector('[data-color-close]').addEventListener('click', () => this.close(true), options);
      this.list.addEventListener('pointerdown', event => { if (event.pointerType !== 'touch') event.preventDefault(); }, options);
      this.list.addEventListener('click', event => {
        const option = event.target.closest('[data-color-index]');
        if (option) this.choose(this.results[Number(option.dataset.colorIndex)]);
      }, options);
      this.popup.addEventListener('keydown', event => {
        if (event.key === 'Escape') { event.preventDefault(); this.close(true); }
      }, options);
      document.addEventListener('pointerdown', event => { if (!this.sharedSheet && !this.contains(event.target)) this.close(); }, options);
      this.addEventListener('focusout', event => { if (!this.sharedSheet && !this.contains(event.relatedTarget)) this.close(); }, options);
      window.visualViewport?.addEventListener('resize', () => this.fitMobileViewport(), options);
      window.visualViewport?.addEventListener('scroll', () => this.fitMobileViewport(), options);
    }
    disconnectedCallback() { if (this.sharedSheet) window.OeVariantSheet?.close(false); this.events?.abort(); }
    updateSelection() {
      this.renderColorLabel(this.querySelector('[data-color-selection]'), this.items.find(item => item.value === this.selected) || { value: this.selected });
      this.paintChip(this.querySelector('[data-color-chip]'), this.selected);
    }
    surchargeFor(item) {
      if (!Number.isSafeInteger(this.basePrice) || !Number.isSafeInteger(item.price)) return 0;
      return Math.max(0, item.price - this.basePrice);
    }
    renderColorLabel(label, item) {
      label.textContent = this.label(item.value);
      const surcharge = this.surchargeFor(item);
      if (surcharge > 0) {
        const amount = document.createElement('em');
        amount.className = 'oe-carrez-color__surcharge';
        amount.textContent = `+${this.money.format(surcharge / 100)}`;
        label.append(' ', amount);
      }
    }
    paintChip(chip, value) {
      const color = window.OeCarrezColors.find(value);
      chip.style.backgroundColor = color?.hex || '';
      chip.classList.toggle('oe-carrez-color__chip--unknown', !color?.hex);
    }
    open() {
      if (this.opened) return;
      this.opened = true;
      this.input.value = '';
      this.popup.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
      this.input.setAttribute('aria-expanded', 'true');
      if (window.OeVariantSheet) {
        this.sharedSheet = true;
        this.popup.removeAttribute('role');
        window.OeVariantSheet.open({
          content: this.popup, trigger: this.trigger, label: 'Farbe', allowDesktop: true,
          initialFocus: this.popup.querySelector('[data-color-close]'),
          onClose: () => {
            this.sharedSheet = false;
            this.popup.setAttribute('role', 'dialog');
            this.close(false);
          },
        });
        this.filter('');
        return;
      }
      this.fitMobileViewport();
      this.filter('');
      this.input.focus({ preventScroll: true });
    }
    close(restoreFocus = false) {
      if (this.sharedSheet) { window.OeVariantSheet.close(restoreFocus); return; }
      this.opened = false;
      this.popup.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.list.replaceChildren();
      if (restoreFocus) this.trigger.focus({ preventScroll: true });
    }
    fitMobileViewport() {
      if (!this.opened || this.sharedSheet) return;
      if (!window.matchMedia('(max-width: 749px)').matches) {
        const rect = this.trigger.getBoundingClientRect();
        const below = window.innerHeight - rect.top - 24;
        const above = rect.bottom - 16;
        const flip = below < 360 && above > below;
        this.popup.style.setProperty('--oe-color-top', flip ? 'auto' : `${this.trigger.offsetTop}px`);
        this.popup.style.setProperty('--oe-color-bottom-desktop', flip ? `${this.offsetHeight - this.trigger.offsetTop - this.trigger.offsetHeight}px` : 'auto');
        this.popup.style.setProperty('--oe-color-desktop-height', `${Math.max(180, Math.min(528, flip ? above : below))}px`);
        return;
      }
      const viewport = window.visualViewport;
      const height = viewport?.height || window.innerHeight;
      this.popup.style.setProperty('--oe-color-height', `${Math.floor(height * 0.78)}px`);
      this.popup.style.setProperty('--oe-color-bottom', `${Math.max(8, window.innerHeight - height - (viewport?.offsetTop || 0) + 8)}px`);
    }
    filter(query) {
      this.results = window.OeCarrezColors.search(this.items, query);
      this.active = Math.max(0, this.results.findIndex(item => item.value === this.selected));
      this.list.replaceChildren();
      const fragment = document.createDocumentFragment();
      this.results.forEach((item, index) => {
        const row = document.createElement('div');
        row.id = `${this.input.id}-result-${index}`;
        row.setAttribute('role', 'option');
        row.setAttribute('aria-selected', String(item.value === this.selected));
        const surcharge = this.surchargeFor(item);
        row.setAttribute('aria-label', this.label(item.value) + (surcharge > 0 ? ` +${this.money.format(surcharge / 100)}` : ''));
        row.dataset.colorIndex = index;
        const chip = document.createElement('span');
        chip.className = 'oe-carrez-color__chip';
        chip.setAttribute('aria-hidden', 'true');
        this.paintChip(chip, item.value);
        const label = document.createElement('span');
        this.renderColorLabel(label, item);
        if (!item.available) label.append(' · derzeit nicht verfügbar');
        row.append(chip, label);
        if (item.value === this.selected) {
          const check = document.createElement('span');
          check.className = 'oe-carrez-color__check';
          check.setAttribute('aria-hidden', 'true');
          const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
          svg.setAttribute('viewBox', '0 0 24 24');
          svg.setAttribute('fill', 'none');
          svg.setAttribute('stroke', 'currentColor');
          svg.setAttribute('stroke-width', '1.8');
          const circle = document.createElementNS(svg.namespaceURI, 'circle');
          circle.setAttribute('cx', '12'); circle.setAttribute('cy', '12'); circle.setAttribute('r', '9');
          const path = document.createElementNS(svg.namespaceURI, 'path');
          path.setAttribute('d', 'm8 12 3 3 5-6');
          path.setAttribute('stroke-linecap', 'round'); path.setAttribute('stroke-linejoin', 'round');
          svg.append(circle, path); check.append(svg); row.append(check);
        }
        fragment.append(row);
      });
      this.list.append(fragment);
      this.popup.querySelector('[data-color-status]').textContent = this.results.length ? `${this.results.length} Farben gefunden` : 'Keine passende Farbe gefunden';
      this.list.scrollTop = 0;
      this.highlight();
    }
    highlight() {
      this.list.querySelector('[data-active]')?.removeAttribute('data-active');
      const active = this.list.children[this.active];
      if (!active) { this.input.removeAttribute('aria-activedescendant'); return; }
      active.dataset.active = '';
      this.input.setAttribute('aria-activedescendant', active.id);
      // Scroll only the internal list; never move the whole PDP.
      const top = active.getBoundingClientRect().top - this.list.getBoundingClientRect().top + this.list.scrollTop - this.list.clientTop;
      if (top < this.list.scrollTop) this.list.scrollTop = top;
      else if (top + active.offsetHeight > this.list.scrollTop + this.list.clientHeight) this.list.scrollTop = top + active.offsetHeight - this.list.clientHeight;
    }
    key(event) {
      if (event.key === 'Escape') { event.preventDefault(); this.close(true); return; }
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End', 'PageDown', 'PageUp', 'Enter'].includes(event.key)) return;
      if (['Home', 'End'].includes(event.key) && !event.ctrlKey) return;
      event.preventDefault();
      if (event.key === 'Enter') { this.choose(this.results[this.active]); return; }
      const moves = { ArrowDown: 1, ArrowUp: -1, PageDown: 8, PageUp: -8 };
      this.active = event.key === 'Home' ? 0 : event.key === 'End' ? this.results.length - 1 : Math.min(this.results.length - 1, Math.max(0, this.active + moves[event.key]));
      this.highlight();
    }
    choose(item) {
      if (!item) return;
      this.selected = item.value;
      this.updateSelection();
      this.close(true);
      let option = Array.from(this.native.options).find(entry => entry.value === item.value);
      if (!option) {
        option = new Option(item.value, item.value);
        option.dataset.optionValueId = item.id;
        option.dataset.productUrl = item.productUrl || '';
        option.dataset.optionName = this.native.name.slice(8, -1);
        option.dataset.focusTarget = this.trigger.id;
        this.native.add(option);
      }
      this.native.value = item.value;
      this.native.dispatchEvent(new Event('change', { bubbles: true }));
      Array.from(this.native.options).forEach(entry => { if (!entry.selected) entry.remove(); });
    }
  });
}
